import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { Inbox } from "lucide-react";
import { Alert } from "./Alert";
import { Button } from "./Button";

interface EmptyStateProps {
  icon?: LucideIcon;
  message: string;
  action?: ReactNode;
}

export function EmptyState({ icon: Icon = Inbox, message, action }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center">
      <Icon size={32} className="text-gray-400" aria-hidden="true" />
      <p className="text-sm text-gray-600">{message}</p>
      {action}
    </div>
  );
}

interface ErrorStateProps {
  message: string;
  onRetry: () => void;
}

export function ErrorState({ message, onRetry }: ErrorStateProps) {
  return (
    <Alert
      type="error"
      message={message}
      actions={
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Retry
        </Button>
      }
    />
  );
}

interface SkeletonProps {
  className?: string;
  children?: never;
}

export function Skeleton({ className }: SkeletonProps) {
  return <div aria-hidden="true" className={`skeleton rounded-md bg-gray-200 ${className ?? "h-4 w-full"}`} />;
}

interface SkeletonStatStripProps {
  tiles: number;
}

export function SkeletonStatStrip({ tiles }: SkeletonStatStripProps) {
  return (
    <div role="status" aria-label="Loading totals" className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-8">
      {Array.from({ length: tiles }, (_, index) => (
        <div key={index} className="rounded-2xl bg-white p-4 shadow-md">
          <Skeleton className="mb-2 h-3 w-16" />
          <Skeleton className="h-7 w-12" />
        </div>
      ))}
    </div>
  );
}

interface SkeletonFormProps {
  fields: number;
}

export function SkeletonForm({ fields }: SkeletonFormProps) {
  return (
    <div role="status" aria-label="Loading form" className="space-y-4 rounded-2xl bg-white p-5 shadow-md">
      {Array.from({ length: fields }, (_, index) => (
        <div key={index}>
          <Skeleton className="mb-2 h-3 w-28" />
          <Skeleton className="h-10 w-full" />
        </div>
      ))}
    </div>
  );
}
